import { type ChangeEvent } from "react";
import { useCanvasStore, SHAPE_PREFIX, type EdgeKind } from "@/store/useCanvasStore";
import { NODE_COLOR_PALETTE } from "@/config/theme";
import { CV } from "@/config/canvasSizing";
import { parseDrawing, serializeDrawing } from "@/utils/drawing";

// ── Primitives ────────────────────────────────────────────────────────────────

interface PropertyFieldProps {
  label: string;
  children: React.ReactNode;
}

function PropertyField({ label, children }: PropertyFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-canvas-muted font-mono font-extrabold">{label}</span>
      {children}
    </div>
  );
}

const inputCls =
  "w-full bg-canvas-bg border border-canvas-node-border rounded px-2 py-1 text-xs text-canvas-text focus:outline-none focus:border-canvas-accent transition-colors";

const badgeCls =
  "inline-block text-[10px] px-1.5 py-0.5 rounded bg-canvas-node-border/60 text-canvas-muted font-mono";

// ── Section panels ────────────────────────────────────────────────────────────

const EDGE_KINDS: EdgeKind[] = ["causes", "part-of", "leads-to", "answered-by"];

function NodePanel({ id, label, type, revealStep, color }: {
  id: string; label: string; type: string; revealStep: number; color?: string;
}) {
  const updateNode = useCanvasStore(s => s.updateNode);
  return (
    <>
      <PropertyField label="Type">
        <span className={badgeCls}>{type}</span>
      </PropertyField>
      <PropertyField label="Label">
        <input
          data-testid="prop-label"
          className={inputCls}
          value={label}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            updateNode(id, { label: e.target.value })
          }
        />
      </PropertyField>
      <PropertyField label="Reveal step">
        <input
          data-testid="prop-reveal"
          type="number"
          min={0}
          className={inputCls}
          value={revealStep}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            updateNode(id, { revealStep: Number(e.target.value) })
          }
        />
      </PropertyField>
      <PropertyField label="Color">
        <div className="flex flex-wrap gap-1.5">
          {NODE_COLOR_PALETTE.map(c => (
            <button
              key={c.name}
              type="button"
              data-testid={`prop-color-${c.name}`}
              aria-label={c.name}
              onClick={() => updateNode(id, { color: c.name })}
              className={`w-4 h-4 rounded-full ${c.swatchClass} ${
                color === c.name ? "ring-2 ring-offset-1 ring-canvas-accent" : ""
              }`}
            />
          ))}
        </div>
      </PropertyField>
    </>
  );
}

function EdgePanel({ id, kind, flowSpeed, flowLabel, revealStep, bidirectional }: {
  id: string; kind?: EdgeKind; flowSpeed: number; flowLabel?: string; revealStep: number; bidirectional?: boolean;
}) {
  const updateEdge = useCanvasStore(s => s.updateEdge);
  return (
    <>
      <PropertyField label="Bidirectional">
        <label className="flex items-center gap-2 text-xs text-canvas-text">
          <input
            data-testid="prop-bidirectional"
            type="checkbox"
            checked={!!bidirectional}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              updateEdge(id, { bidirectional: e.target.checked })
            }
          />
          Dot travels both ways, arrow at both ends
        </label>
      </PropertyField>
      <PropertyField label="Kind">
        <select
          data-testid="prop-kind"
          className={inputCls}
          value={kind ?? "causes"}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            updateEdge(id, { kind: e.target.value as EdgeKind })
          }
        >
          {EDGE_KINDS.map(k => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
      </PropertyField>
      <PropertyField label="Flow speed">
        <input
          data-testid="prop-flow"
          type="number"
          min={0}
          step={0.5}
          className={inputCls}
          value={flowSpeed}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            updateEdge(id, { flowSpeed: Number(e.target.value) })
          }
        />
      </PropertyField>
      <PropertyField label="Flow label">
        <input
          data-testid="prop-flow-label"
          type="text"
          placeholder="e.g. 42"
          className={inputCls}
          value={flowLabel ?? ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            updateEdge(id, { flowLabel: e.target.value || undefined })
          }
        />
      </PropertyField>
      <PropertyField label="Reveal step">
        <input
          type="number"
          min={0}
          className={inputCls}
          value={revealStep}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            updateEdge(id, { revealStep: Number(e.target.value) })
          }
        />
      </PropertyField>
    </>
  );
}

function ShapePanel({ id, kind, revealStep }: {
  id: string; kind: string; revealStep: number;
}) {
  const updateShape = useCanvasStore(s => s.updateShape);
  return (
    <>
      <PropertyField label="Kind">
        <span className={badgeCls}>{kind}</span>
      </PropertyField>
      <PropertyField label="Reveal step">
        <input
          data-testid="prop-shape-reveal"
          type="number"
          min={0}
          className={inputCls}
          value={revealStep}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            updateShape(id, { revealStep: Number(e.target.value) })
          }
        />
      </PropertyField>
    </>
  );
}

function DrawingPanel({ id, content, revealStep }: {
  id: string; content?: string; revealStep: number;
}) {
  const updateShape = useCanvasStore(s => s.updateShape);
  const parsed = parseDrawing(content) ?? { points: [], color: "indigo", width: 4, highlighter: false };

  const patchContent = (patch: Partial<typeof parsed>) =>
    updateShape(id, { content: serializeDrawing({ ...parsed, ...patch }) });

  return (
    <>
      <PropertyField label="Color">
        <div className="flex flex-wrap gap-1.5">
          {NODE_COLOR_PALETTE.map(c => (
            <button
              key={c.name}
              type="button"
              data-testid={`prop-drawing-color-${c.name}`}
              aria-label={c.name}
              onClick={() => patchContent({ color: c.name })}
              className={`w-4 h-4 rounded-full ${c.swatchClass} ${
                parsed.color === c.name ? "ring-2 ring-offset-1 ring-canvas-accent" : ""
              }`}
            />
          ))}
        </div>
      </PropertyField>
      <PropertyField label={`Thickness (${parsed.width})`}>
        <input
          data-testid="prop-drawing-width"
          type="range"
          min={CV.drawing.minWidth}
          max={CV.drawing.maxWidth}
          step={1}
          value={parsed.width}
          onChange={(e: ChangeEvent<HTMLInputElement>) => patchContent({ width: Number(e.target.value) })}
          className="w-full accent-canvas-accent"
        />
      </PropertyField>
      <PropertyField label="Highlighter">
        <label className="flex items-center gap-2 text-xs text-canvas-text">
          <input
            data-testid="prop-drawing-highlighter"
            type="checkbox"
            checked={!!parsed.highlighter}
            onChange={(e: ChangeEvent<HTMLInputElement>) => patchContent({ highlighter: e.target.checked })}
          />
          Semi-transparent
        </label>
      </PropertyField>
      <PropertyField label="Reveal step">
        <input
          data-testid="prop-shape-reveal"
          type="number"
          min={0}
          className={inputCls}
          value={revealStep}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            updateShape(id, { revealStep: Number(e.target.value) })
          }
        />
      </PropertyField>
    </>
  );
}

function MultiPanel({ rfIds }: { rfIds: string[] }) {
  const bulkUpdateRevealStep = useCanvasStore(s => s.bulkUpdateRevealStep);
  return (
    <PropertyField label="Reveal step (all)">
      <input
        data-testid="prop-bulk-reveal"
        type="number"
        min={0}
        className={inputCls}
        placeholder="—"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const v = Number(e.target.value);
          if (!isNaN(v)) bulkUpdateRevealStep(rfIds, v);
        }}
      />
    </PropertyField>
  );
}

// ── PropertiesPanel ───────────────────────────────────────────────────────────

export default function PropertiesPanel() {
  const { selectedIds, rfNodes, rfEdges, rfShapes, activeTool } = useCanvasStore();

  if (selectedIds.size === 0 || activeTool === "hand") return null;

  const selNodeIds  = rfNodes.filter(n => selectedIds.has(n.id));
  const selEdgeIds  = rfEdges.filter(e => selectedIds.has(e.id));
  const selShapeIds = rfShapes.filter(n => selectedIds.has(n.id));

  // Box-selecting nodes makes ReactFlow also select the edges *between* them.
  // Those connecting edges are subsumed by the node selection, so they aren't
  // counted as separate items — only edges bridging to an unselected node are.
  const countedEdges = selEdgeIds.filter(
    e => !(selectedIds.has(e.source) && selectedIds.has(e.target)),
  );

  const total = selNodeIds.length + countedEdges.length + selShapeIds.length;
  const isMulti = total > 1;

  const singleNode  = !isMulti && selNodeIds.length  === 1 ? selNodeIds[0]  : null;
  const singleEdge  = !isMulti && selEdgeIds.length  === 1 ? selEdgeIds[0]  : null;
  const singleShape = !isMulti && selShapeIds.length === 1 ? selShapeIds[0] : null;

  let headerLabel: string;
  if (singleNode)       headerLabel = (singleNode.data.label as string) || "Node";
  else if (singleEdge)  headerLabel = "Edge";
  else if (singleShape) headerLabel = "Shape";
  else                  headerLabel = `${total} items`;

  const allRfIds = [
    ...selNodeIds.map(n => n.id),
    ...selEdgeIds.map(e => e.id),
    ...selShapeIds.map(n => n.id),
  ];

  return (
    <div
      data-testid="properties-panel"
      className="absolute right-3 top-16 w-52 bg-canvas-node border border-canvas-node-border rounded-xl shadow-xl shadow-black/40 p-3 flex flex-col gap-3 z-40"
    >
      <p className="text-xs font-semibold text-canvas-text truncate">{headerLabel}</p>

      {singleNode && (
        <NodePanel
          id={singleNode.id}
          label={singleNode.data.label as string}
          type={singleNode.data.type as string}
          revealStep={singleNode.data.revealStep as number}
          color={singleNode.data.color as string | undefined}
        />
      )}

      {singleEdge && (
        <EdgePanel
          id={singleEdge.id}
          kind={singleEdge.data?.kind as EdgeKind | undefined}
          flowSpeed={singleEdge.data?.flowSpeed as number ?? 0}
          flowLabel={singleEdge.data?.flowLabel as string | undefined}
          revealStep={singleEdge.data?.revealStep as number ?? 0}
          bidirectional={singleEdge.data?.bidirectional as boolean | undefined}
        />
      )}

      {singleShape && (singleShape.data?.kind as string) === "drawing" && (
        <DrawingPanel
          id={singleShape.id.slice(SHAPE_PREFIX.length)}
          content={singleShape.data?.content as string | undefined}
          revealStep={singleShape.data?.revealStep as number ?? 0}
        />
      )}

      {singleShape && (singleShape.data?.kind as string) !== "drawing" && (
        <ShapePanel
          id={singleShape.id.slice(SHAPE_PREFIX.length)}
          kind={singleShape.data?.kind as string ?? ""}
          revealStep={singleShape.data?.revealStep as number ?? 0}
        />
      )}

      {isMulti && <MultiPanel rfIds={allRfIds} />}
    </div>
  );
}
